/**
 * @fileoverview DashboardHeader's own logo/name/tagline block.
 * @description Split out of DashboardHeader purely to keep that component's own render function
 * short — the bank-aware branching (bank badge + name + "Banking" pill + bank tagline, vs. the
 * Sales module's own InsightBox mark + static InsightBox name/tagline) lives here instead.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useTranslation } from 'react-i18next'

import BankBadge from '@/shared/components/atoms/BankBadge'
import SalesBadge from '@/shared/components/atoms/SalesBadge'

import type { DashboardHeaderBank } from '@/shared/components/molecules/DashboardHeader'

interface DashboardHeaderBrandProps {
  bank?: DashboardHeaderBank
}

/**
 * @component
 * @param {DashboardHeaderBrandProps} props - The signed-in Banking-role user's bank identity, if
 * any.
 * @param {DashboardHeaderBank} [props.bank] - Renders the bank's own badge/name/tagline plus a
 * "Banking" pill in place of the Sales module's own InsightBox mark/static name/tagline when
 * present.
 */
function DashboardHeaderBrand({ bank }: DashboardHeaderBrandProps) {
  const { t } = useTranslation()

  return (
    <>
      {bank ? <BankBadge /> : <SalesBadge />}
      <div className="min-w-0 flex-1">
        <p className="flex min-w-0 items-center gap-2">
          <span className="truncate text-[14.5px] font-bold text-ink">
            {bank ? bank.name : t('layout.header.appName')}
          </span>
          {bank && (
            <span className="shrink-0 rounded-full bg-info-bg px-2 py-0.5 text-[10.5px] font-semibold text-info">
              {t('layout.header.bankingBadge')}
            </span>
          )}
        </p>
        <p className="hidden truncate text-xs text-slate sm:block">
          {bank ? bank.tagline : t('layout.header.tagline')}
        </p>
      </div>
    </>
  )
}

export default DashboardHeaderBrand
